import { useState, useEffect, type ReactNode } from 'react';
import { logoDark, logoLight } from '../../assets';

export interface AuthLayoutProps {
  /** Título da tela (`<h1>`) — ex.: "Acesse sua conta". */
  title: string;
  /** Linha de apoio abaixo do título. Opcional. */
  subtitle?: ReactNode;
  /** Conteúdo do card: o formulário da tela. */
  children: ReactNode;
  /** Conteúdo extra **fora** do card, entre ele e o rodapé (ex.: "voltar"). */
  below?: ReactNode;
}

/**
 * Casca das telas de autenticação: fundo, logo que acompanha o tema, título,
 * card e rodapé.
 *
 * Existe porque essa casca era **byte-idêntica** entre `SignInPage`,
 * `ForgotPasswordPage` e `ChangePasswordRequiredPage` — nos dois dashboards. São
 * ~90 linhas × 3 telas × 2 apps, e cada cópia trazia sua própria instância do
 * `MutationObserver` do tema.
 *
 * Mora no design system, e não no pacote de auth, de propósito: é layout e
 * marca, não autenticação. Um pacote de auth não deveria ser dono do logo nem do
 * aviso de copyright.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  below,
}: AuthLayoutProps) {
  /**
   * O tema é uma classe no `<html>` posta por fora deste componente (não há
   * contexto de tema), então a única forma de reagir à troca é observar o
   * atributo. Ver COMPONENT_RULES → "Dark Mode Detection".
   */
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-center">
        <div className="mb-8">
          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-10 w-auto"
          />
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-(--color-text-primary) text-2xl font-bold">
            {title}
          </h1>
          {subtitle && (
            <p className="text-(--color-text-secondary) mt-2 text-sm">
              {subtitle}
            </p>
          )}
        </div>

        <div className="border-(--color-border) bg-(--color-bg-primary) w-full rounded-2xl border p-8 shadow-sm">
          {children}
        </div>

        {below}
      </div>

      <footer className="py-6">
        <p className="text-(--color-text-secondary) text-xs">
          © 2026 Grupo Primum. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}
